import React, { useMemo, useState } from 'react';
import {
  Typography, Box, Collapse, Divider
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { ExpandMore, ExpandLess } from '@material-ui/icons';
import { calculateLaborHours } from './laborCalculator';
import { SERVICE_LABELS, getAreaTypeLabel, LABOR_CATEGORIES } from './siteTakeoffConstants';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  catSection: {
    marginBottom: theme.spacing(1.5),
  },
  catHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 10px',
    borderRadius: 4,
    marginBottom: 4,
  },
  catTitle: {
    fontWeight: 700,
    fontSize: '0.82rem',
    flex: 1,
  },
  catTotal: {
    fontWeight: 700,
    fontSize: '0.82rem',
  },
  serviceRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '2px 10px',
    fontSize: '0.76rem',
    borderBottom: '1px solid #f0f0f0',
  },
  freqBadge: {
    fontSize: '0.63rem',
    fontWeight: 600,
    marginLeft: 4,
  },
  annualBox: {
    padding: '6px 12px',
    backgroundColor: '#f5f5f5',
    borderRadius: 4,
    marginTop: theme.spacing(1),
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    padding: '4px 0',
    '&:hover': { opacity: 0.8 },
  },
  areaHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '4px 8px',
    cursor: 'pointer',
    borderRadius: 4,
    '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)' },
  },
  areaDetail: {
    fontSize: '0.72rem',
    color: '#666',
    paddingLeft: 16,
    paddingBottom: 2,
  },
}));

const formatHours = (h) => {
  if (h === 0) return '0.00';
  if (h < 0.01) return '< 0.01';
  return h.toFixed(2);
};

const freqLabel = (visitsPerYear) => {
  if (visitsPerYear === 1) return '1x/yr';
  if (visitsPerYear === 2) return '2x/yr';
  if (visitsPerYear === 3) return '3x/yr';
  if (visitsPerYear === 4) return 'quarterly';
  if (visitsPerYear === 6) return 'bimonthly';
  if (visitsPerYear === 12) return 'monthly';
  return `${visitsPerYear}x/yr`;
};

// Category display order
const CAT_ORDER = ['mtc', 'arborist', 'herbicide', 'irrigation'];

const LaborEstimate = ({ areas, rateOverrides }) => {
  const classes = useStyles();
  const [byAreaExpanded, setByAreaExpanded] = useState(false);
  const [expandedAreas, setExpandedAreas] = useState({});

  const result = useMemo(
    () => calculateLaborHours(areas, rateOverrides),
    [areas, rateOverrides]
  );

  if (!areas || areas.length === 0) return null;
  if (result.routineHoursPerVisit === 0 && result.periodicServices.length === 0) return null;

  const toggleAreaExpanded = (areaId) => {
    setExpandedAreas(prev => ({ ...prev, [areaId]: !prev[areaId] }));
  };

  return (
    <div className={classes.root}>
      <Typography variant="subtitle2" gutterBottom>Labor Estimate</Typography>

      {/* ═══ PER-CATEGORY SECTIONS ═══ */}
      {CAT_ORDER.map((catKey) => {
        const catData = result.byCategory[catKey];
        if (!catData) return null;
        const catConfig = LABOR_CATEGORIES[catKey] || { label: catKey, color: '#666' };
        const hasRoutine = catData.routinePerVisit > 0;
        const hasPeriodic = catData.periodicServices.length > 0;
        if (!hasRoutine && !hasPeriodic) return null;

        // Routine services for this category
        const routineSvcs = Object.entries(result.routineByService)
          .filter(([, d]) => (d.category || 'mtc') === catKey)
          .sort(([, a], [, b]) => b.totalHoursPerApplication - a.totalHoursPerApplication);

        return (
          <div key={catKey} className={classes.catSection}>
            {/* Category header */}
            <div className={classes.catHeader} style={{ backgroundColor: catConfig.color + '14', borderLeft: `3px solid ${catConfig.color}` }}>
              <span className={classes.catTitle} style={{ color: catConfig.color }}>
                {catConfig.label}
              </span>
              <span className={classes.catTotal} style={{ color: catConfig.color }}>
                {formatHours(catData.annualTotal)} hrs/yr
              </span>
            </div>

            {/* Routine services */}
            {hasRoutine && (
              <Box mb={0.5}>
                <Typography variant="caption" style={{ fontWeight: 600, paddingLeft: 10, color: '#666' }}>
                  Every visit — {formatHours(catData.routinePerVisit)} manhours
                  {catKey === 'mtc' && (
                    <span style={{ fontWeight: 400, marginLeft: 8 }}>
                      ({result.suggestedCrewSize} crew x {formatHours(result.suggestedVisitDuration)} hrs)
                    </span>
                  )}
                </Typography>
                {routineSvcs.map(([svc, data]) => (
                  <div key={svc} className={classes.serviceRow}>
                    <span>
                      {SERVICE_LABELS[svc] || svc}
                      {data.coveragePerVisit < 1 && (
                        <span style={{ fontSize: '0.65rem', fontWeight: 600, color: '#e65100', marginLeft: 4 }}>
                          {Math.round(data.coveragePerVisit * 100)}%
                        </span>
                      )}
                    </span>
                    <span>
                      <strong>{formatHours(data.totalHoursPerApplication)}</strong>
                      <span style={{ color: '#999', marginLeft: 4, fontSize: '0.70rem' }}>
                        ({data.areaCount})
                      </span>
                    </span>
                  </div>
                ))}
              </Box>
            )}

            {/* Periodic services */}
            {hasPeriodic && (
              <Box>
                <Typography variant="caption" style={{ fontWeight: 600, paddingLeft: 10, color: '#666' }}>
                  Per application
                </Typography>
                {catData.periodicServices.map((p) => (
                  <div key={p.service} className={classes.serviceRow}>
                    <span>
                      {SERVICE_LABELS[p.service] || p.service}
                      <span className={classes.freqBadge} style={{ color: catConfig.color }}>
                        {freqLabel(p.visitsPerYear)}
                      </span>
                      {p.coveragePerVisit < 1 && (
                        <span style={{ fontSize: '0.63rem', fontWeight: 600, color: '#e65100', marginLeft: 4 }}>
                          {Math.round(p.coveragePerVisit * 100)}%
                        </span>
                      )}
                    </span>
                    <span>
                      <strong>{formatHours(p.hoursPerApplication)}</strong>
                      <span style={{ color: '#999', marginLeft: 4, fontSize: '0.70rem' }}>
                        ({p.areaCount})
                      </span>
                    </span>
                  </div>
                ))}
              </Box>
            )}
          </div>
        );
      })}

      {/* ═══ ANNUAL SUMMARY ═══ */}
      <div className={classes.annualBox}>
        {CAT_ORDER.map((catKey) => {
          const catData = result.byCategory[catKey];
          if (!catData || catData.annualTotal === 0) return null;
          const catConfig = LABOR_CATEGORIES[catKey] || { label: catKey };
          return (
            <Box key={catKey} display="flex" justifyContent="space-between">
              <Typography variant="caption" style={{ fontWeight: 600, color: catConfig.color }}>
                {catConfig.label}
              </Typography>
              <Typography variant="caption" style={{ fontWeight: 600 }}>
                {formatHours(catData.annualTotal)} hrs/yr
              </Typography>
            </Box>
          );
        })}
        <Divider style={{ margin: '4px 0' }} />
        <Box display="flex" justifyContent="space-between">
          <Typography variant="caption" style={{ fontWeight: 700 }}>Annual Total</Typography>
          <Typography variant="caption" style={{ fontWeight: 700 }}>
            {formatHours(result.grandTotalAnnual)} hrs/yr
          </Typography>
        </Box>
      </div>

      {/* ═══ BY AREA — ALL SERVICES (collapsible) ═══ */}
      <div
        className={classes.sectionHeader}
        onClick={() => setByAreaExpanded(!byAreaExpanded)}
        style={{ marginTop: 8 }}
      >
        <Typography variant="caption" style={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, flex: 1 }}>
          By Area ({result.byArea.filter(a => a.services.length > 0).length})
        </Typography>
        {byAreaExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
      </div>

      <Collapse in={byAreaExpanded}>
        {result.byArea.map((areaData) => {
          if (areaData.services.length === 0) return null;
          const isExpanded = expandedAreas[areaData.areaId];
          // Group services by category
          const routineMtc = areaData.services.filter(s => s.isRoutine && s.category === 'mtc');
          const periodicMtc = areaData.services.filter(s => !s.isRoutine && s.category === 'mtc');
          const arbSvcs = areaData.services.filter(s => s.category === 'arborist');
          const herbSvcs = areaData.services.filter(s => s.category === 'herbicide');
          const irrSvcs = areaData.services.filter(s => s.category === 'irrigation');
          const routineMtcHrs = routineMtc.reduce((s, item) => s + item.hoursPerApplication, 0);
          return (
            <Box key={areaData.areaId} mb={0.5}>
              <div
                className={classes.areaHeader}
                onClick={() => toggleAreaExpanded(areaData.areaId)}
              >
                <Typography style={{ fontSize: '0.78rem' }}>
                  {isExpanded ? '\u25BE' : '\u25B8'}{' '}
                  {areaData.areaName || getAreaTypeLabel(areaData.areaType)}
                </Typography>
                <Typography style={{ fontSize: '0.78rem', fontWeight: 600 }}>
                  {formatHours(routineMtcHrs)} hrs/visit
                </Typography>
              </div>
              <Collapse in={isExpanded}>
                <div className={classes.areaDetail}>
                  {routineMtc.length > 0 && (
                    <div>
                      <span style={{ fontWeight: 600, color: '#1565c0' }}>Routine: </span>
                      {routineMtc.map((s) => (
                        <span key={s.service}>
                          {SERVICE_LABELS[s.service] || s.service} {formatHours(s.hoursPerApplication)}
                          {s.coveragePerVisit < 1 && (
                            <span style={{ color: '#e65100', fontSize: '0.65rem' }}> ({Math.round(s.coveragePerVisit * 100)}%)</span>
                          )}
                          {s.multiplier > 1 && (
                            <span style={{ color: '#e65100' }}> ({s.multiplier.toFixed(2)}x)</span>
                          )}
                          {' \u00B7 '}
                        </span>
                      ))}
                    </div>
                  )}
                  {periodicMtc.length > 0 && (
                    <div>
                      <span style={{ fontWeight: 600, color: '#1565c0' }}>Periodic: </span>
                      {periodicMtc.map((s) => (
                        <span key={s.service}>
                          {SERVICE_LABELS[s.service] || s.service} {formatHours(s.hoursPerApplication)}
                          {s.coveragePerVisit < 1 && (
                            <span style={{ color: '#e65100', fontSize: '0.65rem' }}> ({Math.round(s.coveragePerVisit * 100)}%)</span>
                          )}
                          <span style={{ color: '#7b1fa2', fontSize: '0.65rem' }}> {freqLabel(s.visitsPerYear)}</span>
                          {' \u00B7 '}
                        </span>
                      ))}
                    </div>
                  )}
                  {arbSvcs.length > 0 && (
                    <div>
                      <span style={{ fontWeight: 600, color: '#5d4037' }}>Arborist: </span>
                      {arbSvcs.map((s) => (
                        <span key={s.service}>
                          {SERVICE_LABELS[s.service] || s.service} {formatHours(s.hoursPerApplication)}
                          {s.coveragePerVisit < 1 && (
                            <span style={{ color: '#e65100', fontSize: '0.65rem' }}> ({Math.round(s.coveragePerVisit * 100)}%)</span>
                          )}
                          <span style={{ color: '#7b1fa2', fontSize: '0.65rem' }}> {freqLabel(s.visitsPerYear)}</span>
                          {' \u00B7 '}
                        </span>
                      ))}
                    </div>
                  )}
                  {herbSvcs.length > 0 && (
                    <div>
                      <span style={{ fontWeight: 600, color: '#e65100' }}>Herbicide: </span>
                      {herbSvcs.map((s) => (
                        <span key={s.service}>
                          {SERVICE_LABELS[s.service] || s.service} {formatHours(s.hoursPerApplication)}
                          {s.coveragePerVisit < 1 && (
                            <span style={{ color: '#e65100', fontSize: '0.65rem' }}> ({Math.round(s.coveragePerVisit * 100)}%)</span>
                          )}
                          <span style={{ color: '#7b1fa2', fontSize: '0.65rem' }}> {freqLabel(s.visitsPerYear)}</span>
                          {' \u00B7 '}
                        </span>
                      ))}
                    </div>
                  )}
                  {irrSvcs.length > 0 && (
                    <div>
                      <span style={{ fontWeight: 600, color: '#2e7d32' }}>Irrigation: </span>
                      {irrSvcs.map((s) => (
                        <span key={s.service}>
                          {SERVICE_LABELS[s.service] || s.service} {formatHours(s.hoursPerApplication)}
                          {s.coveragePerVisit < 1 && (
                            <span style={{ color: '#e65100', fontSize: '0.65rem' }}> ({Math.round(s.coveragePerVisit * 100)}%)</span>
                          )}
                          <span style={{ color: '#7b1fa2', fontSize: '0.65rem' }}> {freqLabel(s.visitsPerYear)}</span>
                          {' \u00B7 '}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </Collapse>
            </Box>
          );
        })}
      </Collapse>
    </div>
  );
};

export default React.memo(LaborEstimate);
